import { FormField as VelocityFormField, Text } from '@velocity/ui'
import clsx from 'clsx'
import { type ReactElement, type ReactNode, useEffect, useRef } from 'react'
import { Field, useForm } from 'react-final-form'

import {
  useContent,
  useContentContext,
  useLocalization,
} from '@ngb-frontend/shared/context'
import { scrollToErrorClass } from '@ngb-frontend/shared/utils'

import { useFormFieldStyles } from './FormField.styled'

import type { FormFieldStyles } from '@ngb-frontend/shared/types'
import type { FieldValidator } from 'final-form'
import type { FieldMetaState, FieldInputProps } from 'react-final-form'

export interface FormFieldProps<T = string> {
  /**
   * An custom id to be used for VUI formfield's children
   */
  id?: string
  /**
   * A key to manually trigger field rerenders (rerun validations etc...)
   */
  renderKey?: string
  /**
   * Value for the name attribute of the underlying HTML input.
   */
  name: string
  /**
   * Optional label for the form field.
   */
  label?: ReactNode
  /**
   * Optional hideLabel prop.
   */
  hideLabel?: boolean
  /**
   * Optional HTML for value for the form field.
   */
  htmlFor?: string
  /**
   * Optional FieldValidator for the form field.
   */
  validator?: FieldValidator<T>
  /**
   * Optional Form Field Styles to set the styling of the form control and form label of the field.
   */
  classes?: FormFieldStyles
  /**
   * Optional noMargin when margin should not be applied to the field container
   */
  noMargin?: boolean
  /**
   * Optional noValidation attribute for conditional validations
   */
  noValidation?: boolean
  /**
   * Children as function to render your inputs.
   * @param fieldProps
   */
  children: (fieldProps: {
    input: FieldInputProps<T, HTMLElement>
    meta: FieldMetaState<T>
  }) => ReactElement | ReactElement[]
  /**
   * A hint to display below the field label.
   */
  hint?: string
  /**
   * In case of a Checkbox/RadioGroup, or multiple inputs, group should be set to true.
   */
  group?: true
  /**
   * Optional Required for the form field.
   */
  required?: boolean
  /**
   * Optional disabled for the form field.
   */
  disabled?: boolean
  /**
   * Optional hideOptionalLabel for the form field.
   */
  hideOptionalLabel?: boolean
  /**
   * Optional Type for the form field used by react final form to determine field type.
   */
  type?: string
  /**
   * A custom show error function
   */
  showErrorFn?: <TFieldValue>(
    meta: FieldMetaState<TFieldValue>,
  ) => string | null | undefined
}

/** A field that opened with a value, as opposed to an empty/absent one. */
const arrivedPrefilled = <TFieldValue,>(initial: TFieldValue): boolean => {
  if (initial === undefined || initial === null) return false
  if (Array.isArray(initial)) return initial.length > 0
  if (typeof initial === 'string') return initial.trim() !== ''

  return true
}

/**
 * Errors surface once the field has been touched, changed, or arrived
 * pre-filled with an invalid value. `touched` alone is not enough: the driver
 * journey gates its "Next" button on form validity, and the date/time pickers
 * swallow react-final-form's `onBlur` (see DateField/TimeField), so a field
 * could be invalid with no way left to reveal why — the footer button is
 * disabled and blurring never marks it touched. Pristine empty fields stay
 * quiet; there the disabled button is explanation enough.
 */
function showErrorFnDefaultFactory<TFieldValue>() {
  return (meta: FieldMetaState<TFieldValue>) =>
    meta.error &&
    (meta.touched || meta.modified || arrivedPrefilled(meta.initial))
      ? meta.error.message
      : null
}

export const FormField: <T>(props: FormFieldProps<T>) => ReactElement = ({
  name,
  label = ' ',
  hideLabel,
  htmlFor,
  validator,
  children,
  classes,
  renderKey,
  hint,
  showErrorFn,
  group,
  id,
  hideOptionalLabel = false,
  required = true,
  type,
  noMargin,
  noValidation = false,
  disabled = false,
}) => {
  const ownClasses = useFormFieldStyles({
    hideOptionalLabel,
    noMargin,
    disabled,
  })
  const showError = showErrorFn || showErrorFnDefaultFactory()
  const c = useContent()
  const { content, isContentReady } = useContentContext()
  const { locale } = useLocalization()
  const form = useForm()
  const wasShowingErrorRef = useRef(false)

  const fieldKey = noValidation
    ? 1
    : `${renderKey ?? name}-${isContentReady}-${locale}`

  useEffect(() => {
    if (noValidation || !validator || !wasShowingErrorRef.current) {
      return
    }

    const fieldState = form.getFieldState(name)

    if (!fieldState?.error) {
      wasShowingErrorRef.current = false
      return
    }

    const { value } = fieldState
    form.batch(() => {
      form.change(name, value)
      form.blur(name)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, content, form, name, noValidation])

  return (
    <Field
      name={name}
      type={type}
      /**
       * Based on https://final-form.org/docs/react-final-form/types/FieldProps#validate
       * Known error in the first key change
       * https://github.com/final-form/react-final-form/issues/850
       */
      validate={noValidation ? undefined : validator}
      validateOnMount={false}
      key={fieldKey}
      /* The parse function ensures form values never get set to null,
       * but rather, empty strings.
       *
       * See https://github.com/final-form/react-final-form/issues/130
       */
      parse={(value) => value}
    >
      {({ input, meta }) => {
        if (meta.error && meta.touched) {
          wasShowingErrorRef.current = true
        } else if (!meta.error) {
          wasShowingErrorRef.current = false
        }

        return (
          <div
            className={clsx(ownClasses.root, meta.error && scrollToErrorClass)}
          >
            {hideLabel && !hideOptionalLabel && !required && (
              <Text variant="100" className={ownClasses.optional}>
                {c('fields.shared.optional')}
              </Text>
            )}
            <VelocityFormField
              id={id}
              errorMessage={showError(meta)}
              label={label}
              hideLabel={hideLabel}
              className={clsx(classes?.formControl, ownClasses.field)}
              {...(group ? { group: true } : { inputId: htmlFor })}
              required={required}
            >
              {children({ input, meta })}
            </VelocityFormField>
            {hint && (
              <Text variant="100" className={ownClasses.hint}>
                {hint}
              </Text>
            )}
          </div>
        )
      }}
    </Field>
  )
}
